import type { IRepository } from '@infra/persistence/IRepository';
import { TenantContext, TenantMismatchError } from './TenantContext';
import { assertCanWriteEntity } from './entityWriteGuard';
import type { EntityWriteResource } from '@shared/access/entityWriteRoles';

interface TenantOwned {
  id: string;
  tenant_id: string;
}

/** Garde métier appelée avant toute mutation (ex. période comptable clôturée). Lève pour refuser. */
export type EntityMutationGuard<T> = (entity: T, action: 'create' | 'update' | 'delete') => Promise<void>;

export class TenantScopedRepository<T extends TenantOwned> implements IRepository<T> {
  constructor(
    private readonly inner: IRepository<T>,
    private readonly resource?: EntityWriteResource,
    private readonly mutationGuard?: EntityMutationGuard<T>,
  ) {}
  private ctxId(): string {
    return TenantContext.getInstance().tenantId();
  }

  private assertWrite(action: 'create' | 'update' | 'delete'): void {
    if (this.resource) assertCanWriteEntity(this.resource, action);
  }

  async get(id: string): Promise<T | undefined> {
    const tenantId = this.ctxId();
    const e = await this.inner.get(id);
    if (e && e.tenant_id !== tenantId) throw new TenantMismatchError(tenantId, e.tenant_id);
    return e;
  }
  async list(): Promise<T[]> {
    const tenantId = this.ctxId();
    return this.inner.findBy({ tenant_id: tenantId } as Partial<T>);
  }
  async add(e: T): Promise<void> {
    this.assertWrite('create');
    if (e.tenant_id !== this.ctxId()) throw new TenantMismatchError(this.ctxId(), e.tenant_id);
    await this.mutationGuard?.(e, 'create');
    return this.inner.add(e);
  }
  async update(e: T): Promise<void> {
    this.assertWrite('update');
    if (e.tenant_id !== this.ctxId()) throw new TenantMismatchError(this.ctxId(), e.tenant_id);
    if (this.mutationGuard) {
      const avant = await this.inner.get(e.id);
      if (avant) await this.mutationGuard(avant, 'update');
      await this.mutationGuard(e, 'update');
    }
    return this.inner.update(e);
  }
  async delete(id: string): Promise<void> {
    this.assertWrite('delete');
    const e = await this.inner.get(id);
    if (e && e.tenant_id !== this.ctxId()) throw new TenantMismatchError(this.ctxId(), e.tenant_id);
    if (e) await this.mutationGuard?.(e, 'delete');
    return this.inner.delete(id);
  }

  async findById(id: string): Promise<T | null> {
    const e = await this.get(id);
    return e ?? null;
  }
  async findAll(): Promise<T[]> {
    return this.list();
  }
  async findBy(criteria: Partial<T>): Promise<T[]> {
    const all = await this.list();
    return all.filter((e) =>
      Object.entries(criteria).every(([k, v]) => (e as Record<string, unknown>)[k] === v),
    );
  }
  async save(e: T): Promise<T> {
    this.assertWrite('update');
    if (e.tenant_id !== this.ctxId()) throw new TenantMismatchError(this.ctxId(), e.tenant_id);
    if (this.mutationGuard) {
      const avant = await this.inner.get(e.id);
      if (avant) await this.mutationGuard(avant, 'update');
      await this.mutationGuard(e, avant ? 'update' : 'create');
    }
    await this.inner.save(e);
    return e;
  }
  async saveMany(es: T[]): Promise<T[]> {
    for (const e of es) await this.save(e);
    return es;
  }
  async count(criteria?: Partial<T>): Promise<number> {
    return criteria ? (await this.findBy(criteria)).length : (await this.list()).length;
  }
}
